// pages/components/settings/Preferences.jsx
import React, { useState, useEffect } from 'react';
import { Save, Loader2, Check, Zap, Globe, RefreshCw } from 'lucide-react';
import apiRequest from '@/services/requests';

const Preferences = () => {
const [loading, setLoading] = useState(true);
const [saving, setSaving] = useState(false);
const [saved, setSaved] = useState(false);
const [preferences, setPreferences] = useState({
auto_apply_enabled: false,
package_auto_renew: false,
account_language: 'en'
});

useEffect(() => {
loadPreferences();
}, []);

const loadPreferences = async () => {
setLoading(true);
try {
const res = await apiRequest.getPreferences();
if (res.success) {
// console.log(res.data)
setPreferences(res.data);
}
} catch (error) {
console.error('Error loading preferences:', error);
}
setLoading(false);
};

const handleToggle = (key) => {
setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
setSaved(false);
};

const handleLanguageChange = (e) => {
setPreferences(prev => ({ ...prev, account_language: e.target.value }));
setSaved(false);
};

const handleSave = async () => {
setSaving(true);

try {
const res = await apiRequest.updatePreferences(preferences);
if (res.success) {
setSaved(true);
setTimeout(() => setSaved(false), 3000);
}
} catch (error) {
console.error('Error saving preferences:', error);
alert('Failed to save preferences');
}

setSaving(false);
};

if (loading) {
return (
<div className="flex items-center justify-center py-12">
<Loader2 className="animate-spin text-indigo-600" size={32} />
</div>
);
}

return (
<div className="space-y-8">
<div>
<h2 className="hidden text-2xl font-bold text-slate-900 mb-2">Preferences</h2>
<p className="hidden text-slate-600">Customize your application experience</p>
</div>

<div className="space-y-6">
{/* Auto-Apply Setting */}
<div className="p-6 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-indigo-200">
<div className="flex items-start justify-between">
<div className="flex-1">

<div className="flex items-center justify-between gap-3 mb-2">

<div className="flex items-center gap-3">
<div className="p-2 bg-indigo-100 rounded-lg">
<Zap className="text-indigo-600" size={16} />
</div>
<h3 className="text-md lg:text-lg font-bold text-slate-900">Auto-Apply to Jobs</h3>
</div>

<button
onClick={() => handleToggle('auto_apply_enabled')}
className={`ml-6 relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 ${
preferences.auto_apply_enabled ? 'bg-indigo-600' : 'bg-slate-300'
}`}
>
<span
className={`pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
preferences.auto_apply_enabled ? 'translate-x-6' : 'translate-x-0'
}`}
/>
</button>

</div>


<p className="text-slate-600 text-sm leading-relaxed">
Automatically submit applications to matched jobs that meet your criteria. 
This feature uses your default resume and cover letter templates.
</p>
<div className="mt-3 flex items-center gap-2 text-xs text-indigo-700 bg-indigo-100 px-3 py-1.5 rounded-full w-fit">
<span className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></span>
{preferences.auto_apply_enabled ? 'Currently Active' : 'Currently Inactive'}
</div>
</div>

</div>
</div>



{/* Auto-Renew Setting */}
<div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
<div className="flex items-start justify-between">
<div className="flex-1">

<div className="flex items-center justify-between gap-3 mb-2">

<div className="flex items-center gap-3">
<div className="p-2 bg-green-100 rounded-lg">
<RefreshCw className="text-green-600" size={16} />
</div>
<h3 className="text-md lg:text-lg font-bold text-slate-900">Auto-Renew Subscription</h3>
</div>

<button
onClick={() => handleToggle('package_auto_renew')}
className={`ml-6 relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 ${
preferences.package_auto_renew ? 'bg-green-600' : 'bg-slate-300'
}`}
>
<span
className={`pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
preferences.package_auto_renew ? 'translate-x-6' : 'translate-x-0'
}`}
/>
</button>
</div>


<p className="text-slate-600 text-sm leading-relaxed">
Automatically renew your subscription at the end of each billing cycle. 
You can cancel anytime from the Billing page.
</p>
<div className="mt-3 flex items-center gap-2 text-xs text-green-700 bg-green-100 px-3 py-1.5 rounded-full w-fit">
<span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
{preferences.package_auto_renew ? 'Will Auto-Renew' : 'Manual Renewal'}
</div>
</div>

</div>
</div>



{/* Language Preference */}
<div className="p-6 bg-slate-50 rounded-xl border border-slate-200">
<div className="flex items-start gap-3">

<div className="flex-1">

<div className="flex items-center gap-3">
<div className="p-2 bg-secondary rounded-lg">
<Globe className="text-slate-200" size={16} />
</div>
<h3 className="text-md lg:text-lg font-bold text-slate-900">Language</h3>
</div>

<p className="text-slate-600 text-sm mt-2 mb-4">
Choose your preferred language for the application interface
</p>
<select
value={preferences.account_language}
onChange={handleLanguageChange}
className="px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white"
>
<option value="en">English</option>
<option value="fr">Français</option>
</select>
</div>
</div>
</div>
</div>

{/* Save Button */}
<div className="flex justify-end pt-6 border-t border-slate-200">
<button
onClick={handleSave}
disabled={saving}
className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-semibold rounded-lg shadow-md transition-all"
>
{saving ? (
<>
<Loader2 className="animate-spin" size={20} />
Saving...
</>
) : saved ? (
<>
<Check size={20} />
Saved!
</>
) : (
<>
<Save size={20} />
Save Preferences
</>
)}
</button>
</div>
</div>
);
};

export default Preferences;